import styled, { css, FlattenSimpleInterpolation } from "styled-components";
import { FC, useEffect, useRef } from "react";

import { BaseComponentProps } from "../types";
import { primaryActive, primaryInactive } from "../../typography/alpha";

export enum SwitchTestIds {
  Container = "switch",
}

const Input = styled.input`
  position: absolute;
  height: 0;
  width: 0;
  opacity: 0;
  z-index: -1;
`;

type TransitionType = "transform" | "opacity";

const transition = (
  transitionType: TransitionType
): FlattenSimpleInterpolation => css`
  transition: ${transitionType} cubic-bezier(0.21, 0.92, 0.74, 0.88) 0.2s;
`;

const StyledSwitchTrack = styled.div`
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 12px;
  height: 100%;
  background-color: var(--color-primary-300);
`;

const ActiveBackground = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  transform: translateX(-100%);
  background-color: var(--color-secondary-500);
  ${transition("transform")};
`;

const Circle = styled.div`
  position: absolute;
  z-index: 0;
  top: 1px;
  margin: 0px 1px;
  background-color: var(--color-neutral-000-full);
  height: 18px;
  width: 18px;
  border-radius: 50%;
  transform: translateX(0);
  ${transition("transform")};
  ::after {
    content: "";
    position: absolute;
    height: 100%;
    width: 100%;
    border-radius: inherit;
    box-shadow: var(--elevation-2);
    opacity: 1;
    ${transition("opacity")};
  }
  ::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    height: 1px;
    width: 8px;
    background-color: var(--color-primary-400);
    opacity: 0;
    z-index: 1;
    ${transition("opacity")};
  }
`;

const Marker = styled.div`
  position: relative;
  display: inline-block;
  height: 20px;
  width: 36px;

  ${Input}:disabled + ${StyledSwitchTrack} {
    background-color: var(--color-primary-100);
    ${Circle}::after {
      opacity: 0;
    }
  }

  ${Input}:disabled:checked + ${StyledSwitchTrack} {
    background-color: var(--color-secondary-200);
    ${Circle}::after {
      opacity: 0;
    }
  }

  ${Input}:checked + ${StyledSwitchTrack} {
    ${Circle} {
      transform: translateX(calc(100% - 2px));
    }
  }

  ${Input}:not(:disabled):checked + ${StyledSwitchTrack} ${ActiveBackground} {
    transform: translateX(0);
  }

  ${Input}:indeterminate + ${StyledSwitchTrack} {
    ${Circle} {
      transform: translateX(calc(50% - 1px));
    }
    ${Circle}::before {
      opacity: 1;
    }
  }

  ${Input}:not(:disabled):indeterminate + ${StyledSwitchTrack} ${ActiveBackground} {
    transform: translateX(0);
  }
`;

type LabelProps = {
  $disabled?: boolean;
};

const Label = styled.label<LabelProps>`
  display: grid;
  grid-template-columns: min-content max-content;
  grid-template-areas: "marker label";
  align-items: center;
  width: max-content;
  ${({ $disabled }) =>
    $disabled ? primaryInactive.subtitle3 : primaryActive.subtitle3};
  cursor: ${({ $disabled }): string => ($disabled ? "not-allowed" : "pointer")};

  span {
    grid-area: label;
    user-select: none;
    margin-left: 4px;
  }

  .switch-track {
    grid-area: marker;
  }
`;

type Props = BaseComponentProps<"input"> & {
  id: string;
  testId?: string;
  className?: string;
  label?: string;
  indeterminate?: boolean;
};

export const Switch: FC<Props> = ({
  id,
  testId,
  className,
  label,
  disabled,
  indeterminate,
  ...args
}) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = Boolean(indeterminate);
      if (ref.current.indeterminate) {
        ref.current.checked = false;
      }
    }
  }, [indeterminate]);

  return (
    <Label
      htmlFor={id}
      data-testid={testId ?? SwitchTestIds.Container}
      $disabled={disabled}
      className={className}
    >
      {Boolean(label) && <span>{label}</span>}
      <Marker>
        <Input
          aria-label={label}
          ref={ref}
          id={id}
          type="checkbox"
          {...args}
          disabled={disabled}
        />
        <StyledSwitchTrack className="switch-track">
          <ActiveBackground />
          <Circle />
        </StyledSwitchTrack>
      </Marker>
    </Label>
  );
};
